import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Card,
  Typography,
  Tabs,
  Drawer,
  Table,
  InputNumber,
  Space,
  message,
  Select,
  Input,
  Form,
  DatePicker,
  Dropdown,
  Modal,
} from "antd";
import { ExpenseServices } from "../../../Services/Manage/ExpenseServices";
import TextArea from "antd/es/input/TextArea";
import { ServicetypeService } from "../../../Services/Manage/ServicetypeService";
import { SearchOutlined, DownOutlined } from "@ant-design/icons";
import { Tooltip, Badge } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AddExpense from "../Expenses/AddExpense";
import CreatePO from "./CreatePO";
import SubscriptionDropdown from "../../general/SubscriptionDropdown";
import PoPayments from "../../modals/PoPayments";

const { Title } = Typography;
const { RangePicker } = DatePicker;


const ManagePO = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [originalData, setOriginalData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [messageApi, contextHolder] = message.useMessage();
  const [vendorOption, setVendorOption] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]); // [start, end]
  const navigate = useNavigate();

  const [grandTotal, setGrandTotal] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);

  // Fetch all expenses
  const getAllExpense = async () => {
    try {
      const request = await ExpenseServices.getAllExpense();
      const fetchedData = request.data || [];
      setOriginalData(fetchedData);
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to fetch expenses");
    }
  };

  const handlePayNow = async (record) => {
    try {

      const grand_total = record?.data?.reduce((sum, item) => {
        const price = Number(item.totalprice) || 0;
        const taxPercent = Number(item.tax) || 0;

        const taxAmount = (price * taxPercent) / 100;

        return sum + price + taxAmount;
      }, 0);

      setGrandTotal(grand_total);

      console.log("record", record);
      setSelectedBill(record);

      setPaymentModalOpen(true);

    } catch (error) {
      console.log(error);
    }
  };


  // Fetch vendors
  const GetAllVendors = async () => {
    try {
      const request = await ServicetypeService.getAllVendors();
      setVendorOption(
        request.data.map((item) => ({
          label: item.name,
          value: item.id,
        }))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const onPaymentSuccess = () => {
    setPaymentModalOpen(false);
    setSelectedBill(null);
    getAllExpense();
  };

  useEffect(() => {
    getAllExpense();
    GetAllVendors();
  }, []);

  // Edit, Save, Delete Handlers
  const handleEdit = (record) => {
    setEditingRow(record.id);
    setEditingValues({
      description: record.description,
      qty: record.qty,
      unitprice: record.unitprice,
      unit: record.unit,
      tax_amount: record.tax_amount,
      expense_date: record.expense_date
        ? dayjs(record.expense_date).format("YYYY-MM-DD")
        : "",
      subscription_type: record.subscription_type || "",
    });
  };

  const handleSave = async (record) => {
    try {
      const qty = editingValues.qty || 0;
      const unitPrice = editingValues.unitprice || 0;
      const taxPercent = editingValues.tax_amount || 0;

      const taxPerUnit = unitPrice * (taxPercent / 100);
      const unitPriceWithTax = unitPrice + taxPerUnit;

      const updatedRecord = {
        description: editingValues.description,
        qty: qty,
        tax_amount: taxPercent,
        unit: editingValues.unit,
        unitprice: unitPrice,
        totalprice: qty * unitPriceWithTax,
        expense_date: editingValues.expense_date || record.expense_date,
        subscription_type: editingValues.subscription_type || record.subscription_type,
      };

      const request = await ExpenseServices.updateExpense(updatedRecord, record.id);
      if (request.status) {
        messageApi.success(request.message);
        getAllExpense();
      } else {
        messageApi.warning(request.message);
      }
    } catch (error) {
      console.error(error);
      messageApi.error("Update failed");
    }
    setEditingRow(null);
    setEditingValues({});
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditingValues({});
  };

  const handleDelete = async (record) => {
    try {
      const request = await ExpenseServices.deleteExpense(record.id);
      if (request.status) {
        messageApi.success(request.message);
        getAllExpense();
      } else {
        messageApi.warning(request.message);
      }
    } catch (error) {
      console.error(error);
    }
  };



  // Filters: Search + Date Range
  const filteredData = useMemo(() => {
    let filtered = [...originalData];
    const lowerSearch = searchText.toLowerCase();
    const [start, end] = dateRange;

    return filtered
      .map((group) => {
        if (group.type === "other") {
          const filteredExpenses = group.data.filter((exp) => {
            const vendorName = vendorOption.find((v) => v.value === exp.vendor_id)?.label || "";
            const desc = exp.description || "";
            const poId = exp.po_id || "";
            const matchesSearch =
              desc.toLowerCase().includes(lowerSearch) ||
              vendorName.toLowerCase().includes(lowerSearch) ||
              poId.toLowerCase().includes(lowerSearch);

            const addedOn = exp.added_on ? new Date(exp.added_on) : null;
            const matchesDate =
              !start || !end || !addedOn || (addedOn >= start && addedOn <= end);

            return matchesSearch && matchesDate;
          });

          return filteredExpenses.length > 0 ? { ...group, data: filteredExpenses } : null;
        }

        if (group.type === "project") {
          const project = group.project;
          const projectMatch =
            project?.pro_name?.toLowerCase().includes(lowerSearch) ||
            project?.pro_no?.toLowerCase().includes(lowerSearch) ||
            project?.pro_description?.toLowerCase().includes(lowerSearch);

          const filteredSub = group.data.filter((exp) => {
            const vendorName = vendorOption.find((v) => v.value === exp.vendor_id)?.label || "";
            const desc = exp.description || "";
            const matchesSearch =
              desc.toLowerCase().includes(lowerSearch) ||
              vendorName.toLowerCase().includes(lowerSearch);

            const addedOn = exp.added_on ? new Date(exp.added_on) : null;
            const matchesDate =
              !start || !end || !addedOn || (addedOn >= start && addedOn <= end);

            return matchesSearch && matchesDate;
          });

          if (projectMatch || filteredSub.length > 0) {
            return { ...group, data: filteredSub };
          }
          return null;
        }
        return null;
      })
      .filter(Boolean);
  }, [originalData, searchText, dateRange, vendorOption]);

  // Group General Expenses by PO ID
  const groupedGeneralExpenses = useMemo(() => {
    const generalGroup = filteredData.find((item) => item.type === "other");
    if (!generalGroup?.data?.length) return [];

    const grouped = generalGroup.data.reduce((acc, exp) => {
      const key = exp.po_id || "N/A";
      if (!acc[key]) {
        acc[key] = {
          po_id: key,
          vendor_name: vendorOption.find((v) => v.value === exp.vendor_id)?.label || "Unknown",
          data: [],
          vendor_id: exp.vendor_id,
          payments: exp.payments || [],
          added_on: exp.added_on,
          updated_on: exp.updated_on,
        };
      }
      acc[key].data.push(exp);
      return acc;
    }, {});

    return Object.values(grouped);
  }, [filteredData, vendorOption]);

  const filteredProjects = filteredData.filter((item) => item.type === "project");

  // Columns
  // Helper function to calculate totals for a PO group
  const calculatePoTotals = (poRecord) => {
    // 1. Grand Total = sum of (totalprice + tax amount)
    const grandTotal = poRecord.data.reduce((sum, item) => {
      const price = Number(item.totalprice) || 0;
      const taxPercent = Number(item.tax) || 0;
      const taxAmount = (price * taxPercent) / 100;
      return sum + price + taxAmount;
    }, 0);

    // 2. Total Paid = sum of all payments[].paid_amt inside this PO
    // const paidAmount = poRecord.data.reduce((sum, item) => {
    //   if (Array.isArray(item.payments)) {
    //     return (
    //       sum +
    //       item.payments.reduce((pSum, payment) => pSum + Number(payment.paid_amt || 0), 0)
    //     );
    //   }
    //   return sum;
    // }, 0);
    const paidAmount =
      poRecord.payments?.reduce(
        (sum, p) => sum + Number(p.paid_amt || 0),
        0
      ) || 0;

    const pendingAmount = grandTotal - paidAmount;

    // 3. Payment Status
    let status = "Unpaid";
    if (paidAmount >= grandTotal && grandTotal > 0) {
      status = "Paid";
    } else if (paidAmount > 0) {
      status = "Partially Paid";
    }

    return { grandTotal, paidAmount, pendingAmount, status };
  };

  const handleEditPO = (record) => {
    setEditRecord(record);   // record = full PO group
    setDrawerOpen(true);
  };


  // Updated vendorColumns (the main PO table)
  const vendorColumns = [
    {
      title: "PO ID",
      dataIndex: "po_id",
      key: "po_id",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Total Items",
      key: "total_items",
      align: "center",
      render: (_, record) => record.data.length,
    },

    // Grand Total (items + tax)
    {
      title: "Grand Total",
      key: "grand_total",
      align: "right",
      render: (_, record) => {
        const { grandTotal } = calculatePoTotals(record);
        return <strong>{grandTotal.toFixed(2)}</strong>;
      },
    },

    // Total Paid Amount
    // {
    //   title: "Paid Amount",
    //   key: "paid_amount",
    //   align: "right",
    //   render: (_, record) => {
    //     const { paidAmount } = calculatePoTotals(record);
    //     return <span style={{ color: "#52c41a" }}>{paidAmount.toFixed(2)}</span>;
    //   },
    // },

    // Pending Amount
    // {
    //   title: "Pending Amount",
    //   key: "pending_amount",
    //   align: "right",
    //   render: (_, record) => {
    //     const { pendingAmount } = calculatePoTotals(record);
    //     return pendingAmount > 0 ? (
    //       <span style={{ color: "#fa541c" }}>{pendingAmount.toFixed(2)}</span>
    //     ) : (
    //       <span>0.00</span>
    //     );
    //   },
    // },

    // Payment Status Badge
    // {
    //   title: "Payment Status",
    //   key: "payment_status",
    //   align: "center",
    //   render: (_, record) => {
    //     const { status } = calculatePoTotals(record);
    //     let color = "default";
    //     if (status === "Paid") color = "success";
    //     else if (status === "Partially Paid") color = "warning";
    //     else color = "error";

    //     return <Badge status={color} text={status} />;
    //   },
    // },

    // Existing columns (you can keep the rest as they are)

    {
      title: "Last Change",
      key: "updated_on",
      align: "center",
      render: (_, record) =>
        record.data[0]?.updated_on
          ? new Date(record.data[0].updated_on).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "—",
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Button onClick={() => handleEditPO(record)} type="primary">
          Edit
        </Button>
      ),
    },
  ];

  const generalColumns = [
    { title: "S. No", key: "si_no", render: (_, __, i) => i + 1, align: "center" },
    {
      title: "Description",
      dataIndex: "description",
      render: (_, record) =>
        editingRow === record.id ? (
          <TextArea
            value={editingValues.description}
            onChange={(e) => setEditingValues((p) => ({ ...p, description: e.target.value }))}
          />
        ) : (
          record.description
        ),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      render: (_, record) =>
        editingRow === record.id ? (
          <InputNumber
            value={editingValues.qty}
            onChange={(val) => setEditingValues((p) => ({ ...p, qty: val }))}
          />
        ) : (
          record.qty
        ),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      render: (_, record) =>
        editingRow === record.id ? (
          <Input
            value={editingValues.unit}
            onChange={(e) => setEditingValues((p) => ({ ...p, unit: e.target.value }))}
          />
        ) : (
          record.unit
        ),
    },
    {
      title: "tax (%)",
      dataIndex: "tax_amount",
      render: (_, record) =>
        editingRow === record.id ? (
          <Input
            value={editingValues.tax_amount}
            onChange={(e) => setEditingValues((p) => ({ ...p, tax_amount: e.target.value }))}
          />
        ) : (
          record.tax_amount
        ),
    },
    // {
    //   title: "Subscription Date",
    //   dataIndex: "expense_date",
    //   render: (_, record) =>
    //     editingRow === record.id ? (
    //       <input
    //         type="date"
    //         value={editingValues.expense_date || ""}
    //         onChange={(e) => setEditingValues((p) => ({ ...p, expense_date: e.target.value }))}
    //         style={{ width: "100%", padding: "4px", border: "1px solid #d9d9d9", borderRadius: 6 }}
    //       />
    //     ) : record.expense_date ? (
    //       new Date(record.expense_date).toLocaleDateString("en-CA")
    //     ) : (
    //       ""
    //     ),
    // },
    // {
    //   title: "Alert",
    //   align: "center",
    //   render: (_, record) => {
    //     if (!record.expense_date) return null;
    //     const today = new Date();
    //     const due = new Date(record.expense_date);
    //     const diffDays = Math.floor((due - today) / (1000 * 60 * 60 * 24));
    //     if (diffDays >= 0 && diffDays <= 7) {
    //       return (
    //         <Tooltip title={`Due on ${due.toLocaleDateString("en-CA")}`}>
    //           <Badge color="red" style={{ transform: "scale(1.5)" }} />
    //         </Tooltip>
    //       );
    //     }
    //     return null;
    //   },
    // },
    // {
    //   title: "Subscription Type",
    //   width: "14%",
    //   align: "center",
    //   render: (_, record) =>
    //     editingRow === record.id ? (
    //       <SubscriptionDropdown
    //         value={editingValues.subscription_type}
    //         onChange={(val) => setEditingValues((p) => ({ ...p, subscription_type: val }))}
    //       />
    //     ) : (
    //       record.subscription_type
    //     ),
    // },
    {
      title: "Unit Price",
      dataIndex: "unitprice",
      render: (_, record) =>
        editingRow === record.id ? (
          <InputNumber
            value={editingValues.unitprice}
            onChange={(val) => setEditingValues((p) => ({ ...p, unitprice: val }))}
          />
        ) : (
          record.unitprice
        ),
    },
    {
      title: "Total",
      render: (_, record) =>
        editingRow === record.id
          ? (editingValues.qty || 0) * (editingValues.unitprice || 0)
          : record.totalprice,
    },
    {
      title: "Action",
      render: (_, record) =>
        editingRow === record.id ? (
          <Space>
            <Button onClick={() => handleSave(record)}>Save</Button>
            <Button danger onClick={handleCancel}>Cancel</Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => handleEdit(record)}>Edit</Button>
            <Button danger onClick={() => handleDelete(record)}>Delete</Button>
          </Space>
        ),
    },
  ];

  const subColumns = [...generalColumns]; // Same as general for project sub-items
  const projectColumns = [
    { title: "S. No", key: "si_no", render: (_, __, i) => i + 1, align: "center" },
    { title: "Project Name", dataIndex: ["project", "pro_name"] },
    { title: "Project No.", dataIndex: ["project", "pro_no"] },
    { title: "Project Description", dataIndex: ["project", "pro_description"] },
    { title: "Total Items", align: "center", render: (_, r) => r.data?.length || 0 },
    {
      title: "Total Expense",
      align: "right",
      render: (_, r) =>
        Number(r.data?.reduce((s, i) => s + Number(i.totalprice || 0), 0) || 0).toFixed(2),
    },
    {
      title: "Added On",
      align: "center",
      render: (_, r) =>
        r.data?.[0]?.added_on
          ? new Date(r.data[0].added_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
          : "—",
    },
    {
      title: "Updated On",
      align: "center",
      render: (_, r) =>
        r.data?.[0]?.updated_on
          ? new Date(r.data[0].updated_on).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
          : "—",
    },
  ];

  const handleAdd = () => {
    setEditRecord(null);
    setDrawerOpen(true);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setDateRange([null, null]);
  };

  return (
    <>
      {contextHolder}

      <Modal
        width={800}
        title={<Typography.Title level={4}>Add Payment for: {selectedBill?.project?.pro_no || selectedBill?.po_id || selectedBill?.type}</Typography.Title>}
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        {selectedBill && (
          <PoPayments
            getAllExpense={getAllExpense}
            quotationNo={selectedBill?.po_id}
            grandTotal={grandTotal}
            onSuccess={onPaymentSuccess}
          />
        )}
      </Modal>

      <Card
        title={<Title level={4} style={{ margin: 0 }}>Manage Purchase Order</Title>}
        extra={
          <Space>
            <Button size="large" onClick={() => navigate("/manage-vendor")}>
              Add Vendor
            </Button>
            <Button size="large" type="primary" onClick={handleAdd}>
              Create Purchase Order
            </Button>
          </Space>
        }
      >
        <Card style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              allowClear
              placeholder="Search PO, Vendor, Description, Project..."
              size="large"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 400 }}
            />
            <RangePicker
              size="large"
              format="DD MMM YYYY"
              onChange={(dates) => {
                if (dates) {
                  setDateRange([dates[0].startOf("day").toDate(), dates[1].endOf("day").toDate()]);
                } else {
                  setDateRange([null, null]);
                }
              }}
              value={
                dateRange[0] && dateRange[1]
                  ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                  : null
              }
            />
            <Button size="large" onClick={handleClearFilters}>Clear Filters</Button>
          </Space>
        </Card>

        <Table
          rowKey="po_id"
          dataSource={groupedGeneralExpenses}
          columns={vendorColumns}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                rowKey="id"
                dataSource={record.data}
                columns={generalColumns}
                pagination={false}
                size="small"
                summary={(data) => {
                  let totalQty = 0;
                  let subTotal = 0;
                  let totalTax = 0;

                  data.forEach(({ qty, unitprice, tax_amount }) => {
                    const q = Number(qty) || 0;
                    const price = Number(unitprice) || 0;
                    const taxPercent = Number(tax_amount) || 0;

                    const rowSubTotal = q * price;
                    const rowTax = (rowSubTotal * taxPercent) / 100;

                    totalQty += q;
                    subTotal += rowSubTotal;
                    totalTax += rowTax;
                  });

                  const grandTotal = subTotal + totalTax;

                  return (
                    <Table.Summary align="right">
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={7}>
                          <div style={{ textAlign: "right" }}>
                            <Typography.Text strong>
                              Total Qty: {totalQty}
                            </Typography.Text>
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>

                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={7}>
                          <div style={{ textAlign: "right" }}>
                            <Typography.Text strong>
                              Sub Total: {subTotal.toFixed(2)}
                            </Typography.Text>
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>

                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={7}>
                          <div style={{ textAlign: "right" }}>
                            <Typography.Text strong>
                              Total Tax: {totalTax.toFixed(2)}
                            </Typography.Text>
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>

                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={7}>
                          <div style={{ textAlign: "right" }}>
                            <Typography.Text strong>
                              Grand Total: {grandTotal.toFixed(2)}
                            </Typography.Text>
                          </div>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>

                  );
                }}

              />
            ),
          }}
          pagination={{ pageSize: 10 }}
        />

      </Card>

      <Drawer
        title={
          editRecord ? (
            <Title level={4} style={{ margin: 0 }}>Edit Purchase Order</Title>
          ) : (
            <Title level={4} style={{ margin: 0 }}>Create Purchase Order</Title>
          )
        }
        destroyOnClose
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={1600}

      >
        <CreatePO getAllExpense={getAllExpense}
          onClose={() => setDrawerOpen(false)}
          editPO={editRecord}
        />
      </Drawer>
    </>
  );
};

export default ManagePO;